import { detect } from "detect-browser";

import { TWasmBaseMaps, TWasmInteractionType, TWasmOverlay } from "~/interfaces";
import { IGeometryStyle, IMapStyle, IPoiData, ISmapGeometryStyle } from "./interfaces";
import { IStoreItem } from "./store/interfaces";
import { MAP_OVERLAYS } from "~/state/map/constants";
import { LAYER_MAPPING, TARGET_FPS } from "./const";
import { CONFIG } from "~/config";
import Wasm from "./wasm";

let lastStoreId = 0;
let lastCustomId = 0;
let canvasCtx: CanvasRenderingContext2D = null;
const EMPTY_SIZE = 100;

function parseColor(color: string) {
	if (color.includes("#")) {
		return color;
	}

	// fallback - prevede nazvy + rgb na hash, ale bacha rgba -> rgba
	if (!canvasCtx) {
		const canvas = document.createElement("canvas");

		canvasCtx = canvas.getContext("2d");
	}

	canvasCtx.fillStyle = color;

	return canvasCtx.fillStyle;
}

export function getMapContentStoreId(): IStoreItem["id"] {
	const newId = lastStoreId;

	lastStoreId++;

	return newId;
}

export function getMapContentCustomId(): IStoreItem["id"] {
	const newId = lastCustomId;

	lastCustomId++;

	return newId;
}

export function poiInfoToJson(poiInfo: Module.IPoiInfo, wasm: Wasm): IPoiData {
	let geomType: IPoiData["geomType"] = "Line";

	if (poiInfo.geomType === wasm.module.GeometryType.Point) {
		geomType = "Point";
	} else if (poiInfo.geomType === wasm.module.GeometryType.Polygon) {
		geomType = "Polygon";
	} else if (poiInfo.geomType === wasm.module.GeometryType.Unknown) {
		geomType = "Unknown";
	}

	return {
		id: poiInfo.id,
		source: poiInfo.source,
		title: poiInfo.title,
		mark: {
			lon: poiInfo.markWgs.x,
			lat: poiInfo.markWgs.y,
		},
		markMerc: {
			lon: poiInfo.markMerc.x,
			lat: poiInfo.markMerc.y,
		},
		position: {
			lon: poiInfo.contactPosWgs.x,
			lat: poiInfo.contactPosWgs.y,
		},
		positionMerc: {
			lon: poiInfo.contactPosMerc.x,
			lat: poiInfo.contactPosMerc.y,
		},
		subtitle: poiInfo.subtitle,
		typeId: BigInt(poiInfo.iconSource?.id ?? 0),
		distance: poiInfo.distance,
		geomType,
	};
}

// opacity 0-1
// 0-1
export function decOpacityToHex(opacity: number) {
	const hexValue = (opacity / 1) * 255 >>> 0;

	return hexValue.toString(16);
}

// opacity 0-1
export function setOpacity(hashColor: string, opacity: number): string {
	return `${hashColor}${decOpacityToHex(opacity)}`;
}

export function getRemovedIds(allIds: Array<IStoreItem["id"]>, usedIds: Array<IStoreItem["id"]>) {
	return allIds.filter(item => !usedIds.includes(item));
}

export function getRandomIcon(scaleArg = 1) {
	const random = 1 + (Math.random() * 10 >>> 0);
	let scale = scaleArg;

	if (scale === 1.5) {
		scale = 2;
	}

	return `https://api.mapy.com/poiimg/icon/${random}?scale=${scale}`;
}

export function canvasToDataArray(canvas: HTMLCanvasElement): Promise<ArrayBuffer> {
	return new Promise(resolve => {
		canvas.toBlob(blob => {
			const reader = new FileReader();

			reader.addEventListener("loadend", () => {
				const arrayBuffer = reader.result;

				resolve(arrayBuffer as ArrayBuffer);
			});
			reader.readAsArrayBuffer(blob);
		}, "image/png");
	});
}

export function getWasmIntercationType(module: typeof Module, interactionType: TWasmInteractionType) {
	let retInteractionType = module.InteractionType.None;

	switch (interactionType) {
		case "click":
			retInteractionType = module.InteractionType.OnClick;
			break;

		case "hover":
			retInteractionType = module.InteractionType.OnHover;
			break;

		case "click-hover":
			retInteractionType = module.InteractionType.OnClickAndHover;
			break;

		default:
	}

	return retInteractionType;
}

export function getImage(src: string, crossOrigin?: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();

		img.addEventListener("load", () => {
			resolve(img);
		});
		img.addEventListener("error", () => {
			reject(img);
		});

		if (crossOrigin) {
			img.crossOrigin = crossOrigin;
		}

		img.src = src;
	});
}

// Blend formula: result = fg * opacity + bg * (1 - opacity)
function blend(fgChannel: number, bgChannel: number, opacity: number) {
	return Math.round((fgChannel * opacity) + (bgChannel * (1 - opacity)))
		.toString(16)
		.padStart(2, "0");
}

export function blendHexColors(fgHexArg: string, bgHexArg: string, opacity: number): string {
	const fgHex = fgHexArg.replace(/^#/u, "");
	const bgHex = bgHexArg.replace(/^#/u, "");
	const fg = {
		r: parseInt(fgHex.slice(0, 2), 16),
		g: parseInt(fgHex.slice(2, 4), 16),
		b: parseInt(fgHex.slice(4, 6), 16),
	};
	const bg = {
		r: parseInt(bgHex.slice(0, 2), 16),
		g: parseInt(bgHex.slice(2, 4), 16),
		b: parseInt(bgHex.slice(4, 6), 16),
	};

	return `#${blend(fg.r, bg.r, opacity)}${blend(fg.g, bg.g, opacity)}${blend(fg.b, bg.b, opacity)}`;
}

export function getGeometryStyleHash(style: IGeometryStyle) {
	return [style.color, style.width, style.edgeColor || "#000", style.edgeWidth || 0, style.lineCap || "round", style.lineJoin || "round", style.keepExactWidth ? "exact-true" : "exact-false", style.dashed || ""].join("|");
}

export function parseGeometryStyleHash(hash: string): IGeometryStyle | null {
	const parts = hash.split("|");

	if (parts.length === 8) {
		const dashedValue = parts[7];
		// mame data - carka, je plny, jinak opakuj 4x
		const dashed = dashedValue
			? dashedValue.includes(",")
				? dashedValue
				: `${dashedValue},${dashedValue},${dashedValue},${dashedValue}`
			: "";

		return {
			color: parts[0],
			width: parseFloat(parts[1]),
			edgeColor: parts[2],
			edgeWidth: parseFloat(parts[3]),
			lineCap: parts[4] as IGeometryStyle["lineCap"],
			lineJoin: parts[5] as IGeometryStyle["lineJoin"],
			keepExactWidth: parts[6] === "exact-true",
			dashed,
		};
	}

	return null;
}

export function convertSmapStyleToWasmGeometryStyleHash(smapStyle: ISmapGeometryStyle, blendColors?: boolean) {
	const blendBgColor = "#ffffff";
	let color = parseColor(smapStyle.color || "#000");
	let edgeColor = parseColor(smapStyle.outlineColor || "#000");
	let dashed = "";

	if ("opacity" in smapStyle && smapStyle.opacity < 1) {
		color = blendColors
			? blendHexColors(color, blendBgColor, smapStyle.opacity)
			: setOpacity(color, smapStyle.opacity);
	}

	if ("outlineOpacity" in smapStyle && smapStyle.outlineOpacity < 1) {
		edgeColor = blendColors
			? blendHexColors(edgeColor, blendBgColor, smapStyle.outlineOpacity)
			: setOpacity(edgeColor, smapStyle.outlineOpacity);
	}

	if (smapStyle.exactStyle) {
		// odstranime px
		const exactStyle = smapStyle.exactStyle.replace(/px/ug, "");
		const parts = exactStyle.split(" ");
		let output: Array<string> = [];
		let ptr = 0;

		if (parts.length === 4) {
			output = parts;
		} else {
			for (let ind = 0; ind < 4; ind++) {
				output.push(parts[ptr % parts.length]);
				ptr++;
			}
		}

		dashed = output.join(" ");
	}

	const style: IGeometryStyle = {
		color,
		// musi se zvetsit - puvodne * 1.5, nyni + 2px
		width: Math.floor((smapStyle.width || 0) + 2),
		edgeColor: edgeColor,
		edgeWidth: smapStyle.outlineWidth,
		keepExactWidth: smapStyle.keepExactWidth ?? true,
		dashed,
		lineCap: smapStyle.endCap === 2
			? "square"
			: "round",
		lineJoin: "round",
	};

	return getGeometryStyleHash(style);
}

export function updateApiUrlSize(iconUrl: string, scale: number) {
	const url = new URL(iconUrl);

	url.searchParams.set("scale", scale.toString());

	return url.toString();
}

// https://api.mapy.com/poiimg/icon/11?scale=1
export function isUrlIcon(url: string) {
	return url.match(/poiimg\/icon\/\d+/u);
}

const FORMAT_SIZE_VARIANTS = ["", "K", "M", "G", "T"];

/**
 * Format size in bytes.
 */
export function formatSize(size: number) {
	if (typeof size !== "number") {
		return "";
	}

	const lv = size > 0
		? Math.floor(Math.log(size) / Math.log(1024))
		: 0;
	const lv2 = Math.min(FORMAT_SIZE_VARIANTS.length, lv);
	const value = lv2 > 0
		? (size / Math.pow(1024, lv2)).toFixed(2)
		: size;

	return `${value} ${FORMAT_SIZE_VARIANTS[lv2]}B`;
}

export function getMapStyleObject(mapset: TWasmBaseMaps, overlays: Array<MAP_OVERLAYS> = [], ophotoTimeline = "", desaturate: boolean): IMapStyle {
	let base: TWasmBaseMaps = mapset;

	// fallback
	if (!LAYER_MAPPING[base]) {
		const filtered = CONFIG.mapsets.filter(item => item.seo === mapset);
		let wasSet = false;

		if (filtered.length > 0) {
			const item = filtered[0];
			const baseItem = CONFIG.mapsets.filter(filterItem => filterItem.base === item.base)[0];

			if (baseItem) {
				base = baseItem.seo as TWasmBaseMaps;
				wasSet = true;
			}
		}

		if (!wasSet) {
			base = "zakladni";
		}
	}

	return {
		base,
		overlays: [
			...overlays.includes(MAP_OVERLAYS.HILLSHADE) ? ["hillshade", "hillslope"] : [],
			...overlays.includes(MAP_OVERLAYS.PANORAMA) ? ["panoLayer"] : [],
			...overlays.includes(MAP_OVERLAYS.CADASTRAL) ? ["knLayer"] : [],
			...overlays.includes(MAP_OVERLAYS.TURIST) ? ["touristMode"] : [],
		] as Array<TWasmOverlay>,
		trafficOverlay: overlays.includes(MAP_OVERLAYS.TRAFFIC),
		ophotoTimeline,
		...desaturate
			? {
				variant: "desaturate",
			}
			: {},
	};
}

export function getMainContainers() {
	const wasm = document.getElementById("wasm");
	const m3d = document.getElementById("left3d");
	const map = document.getElementById("map");

	return {
		map,
		m3d,
		wasm,
	};
}

export function getEmptyImageData(size = EMPTY_SIZE) {
	const data = new Uint8ClampedArray(size * size * 4);

	// Fill with white (255,255,255,255)
	for (let ind = 0; ind < data.length; ind += 4) {
		/* eslint-disable-next-line */
		data[ind]     = 255; // R
		data[ind + 1] = 255; // G
		data[ind + 2] = 255; // B
		data[ind + 3] = 255; // A (fully opaque)
	}

	// Wrap into ImageData
	const imageData = new ImageData(data, size, size);

	return imageData;
}

function getPointDistance(pointA: Array<number>, pointB: Array<number>) {
	return Math.sqrt(Math.pow(pointB[0] - pointA[0], 2) + Math.pow(pointB[1] - pointA[1], 2));
}

// vstupem souradnice v mercatoru
export function getPointAndFraction(allCoords: Array<Array<number>>, testCoord: Array<number>) {
	/* projdeme vsechny pixelove souradnice trasy a najdeme:
		1) nejblizsi bod, NEBO
		2) nejblizsi usek
	*/
	let bestDistance = Infinity;
	let bestPoint: Array<number> = null;
	let bestInd = -1;
	const len = allCoords.length;
	let ind = 0;

	for (const coord of allCoords) {
		// usecka
		if (ind !== len - 1) {
			const nextPoint = allCoords[ind + 1];
			const vector = [
				nextPoint[0] - coord[0],
				nextPoint[1] - coord[1],
			];
			const valK = -((coord[0] * testCoord[0]) - (testCoord[0] * vector[0]) + (coord[1] * vector[1]) - (testCoord[1] * vector[1])) / ((vector[0] * vector[0]) + (vector[1] * vector[1]));

			/* kolmy prumet mysi se nachazi na usecce AB */
			if (valK >= 0 && valK <= 1) {
				vector[0] *= valK;
				vector[1] *= valK;
				vector[0] += coord[0];
				vector[1] += coord[1];

				const dist = getPointDistance(vector, testCoord);

				if (dist < bestDistance) {
					bestDistance = dist;
					bestPoint = vector;
					bestInd = ind;
				}
			}
		}

		// bod
		const pointDist = getPointDistance(coord, testCoord);

		if (pointDist < bestDistance) {
			bestPoint = coord;
			bestDistance = pointDist;
			bestInd = ind;
		}

		ind++;
	}

	return {
		ind: bestInd,
		point: bestPoint,
		distance: bestDistance,
	};
}

export function getWasmDetectionData() {
	const isMobile = Mapy.Util.isMobile();
	let maxFps = TARGET_FPS.chrome;

	if (isMobile) {
		maxFps = TARGET_FPS.mobile;
	} else if (detect().name === "firefox") {
		maxFps = TARGET_FPS.firefox;
	}

	return {
		maxFps,
		isMobile,
	};
}

export function getCorrectDpi(dpi: number) {
	let newDpi = 1;

	if (dpi >= 1 && dpi < 1.4) {
		newDpi = 1;
	} else if (dpi >= 1.4 && dpi < 1.9) {
		newDpi = 1.5;
	} else if (dpi >= 1.9 && dpi < 2.5) {
		newDpi = 2;
	} else if (dpi >= 2.5) {
		newDpi = 3;
	}

	return newDpi;
}

export function isTouchDevice() {
	return window.matchMedia("(pointer: coarse)").matches;
}

export function alignPixel(pixel: number) {
	return Math.round(pixel);
}
